import type { Client } from '@urql/vue';
import { defineStore } from 'pinia';
import { throttle } from 'throttle-debounce';
import { computed, ref } from 'vue';

import type {
  GetSearchSuggestionsNgQuery,
  GetSearchSuggestionsNgQueryVariables,
} from '@/generated/graphql';
import GET_SEARCH_SUGGESTIONS_NG from '@/graphql/getSearchSuggestionsNg.gql';
import getEventHubSessionId from '@/lib/getEventHubSessionId';
import { useNavigationStore } from '@/store/pinia/navigation';

export const useSearchStore = defineStore('useSearchStore', () => {
  const navigationStore = useNavigationStore();

  const query = ref('');
  const queryTrimmed = ref('');
  const queryEnter = ref(false);
  const searchSuggestions = ref<any[]>([]);
  const suggestionsQuery = ref('');
  const requestSuggestions = ref(true);
  const inhibitSuggestions = ref(false);

  const apollo = ref<Client | null>(null);
  const locale = ref('');
  const brandId = ref('');
  const factFinderChannel = ref('');
  const distributionChannels = ref<string[] | null | undefined>(null);
  const supplier = ref<string | undefined>(undefined);
  const publicAssortment = ref<boolean>(false);
  const hasProducts = ref(false);
  const productUrlBase = ref('');
  const productRedirectUrl = ref<string | null>('');

  // Navigation store helpers
  const isNavigationSearch = computed(() => navigationStore.isSearchPage);
  const isFlyoutShown = computed(() => navigationStore.showFlyout);
  const isSearchSuggestShown = computed(() => navigationStore.showSearchSuggest);

  const setShowSuggestionsFlyout = (value: boolean) => {
    navigationStore.setShowSearchSuggest(value);
    navigationStore.setShowFlyout(value);
  };

  const getSearchSuggestionsThrottled = throttle(400, async (queryValue: string) => {
    if (!apollo.value) {
      console.error('No apollo for search!');
      return;
    }

    if (queryValue.length < 2 && !isNavigationSearch.value) {
      setShowSuggestionsFlyout(false);
      return;
    }

    const getSearchSuggestionsByChannel = async (channelSuffix: string) => {
      const client = apollo.value;
      if (!client) {
        return [];
      }
      const { data, error } = await client
        .query<GetSearchSuggestionsNgQuery, GetSearchSuggestionsNgQueryVariables>(
          GET_SEARCH_SUGGESTIONS_NG,
          {
            query: queryValue,
            channelSuffix,
            factFinderChannel: factFinderChannel.value,
            sid: getEventHubSessionId(),
            filters: distributionChannels.value?.length
              ? [
                  {
                    associatedFieldName: 'Sales_Channel_K',
                    name: distributionChannels.value.join('___'),
                  },
                ]
              : null,
            supplier: supplier.value,
            publicAssortment: publicAssortment.value,
          },
          { requestPolicy: 'network-only' },
        )
        .toPromise();
      if (error) {
        console.error('No apollo for search!', error);
        return [];
      }
      // Both results are spread into one list below, so every exit has to be an array.
      const { getSearchSuggestionsNg } = data ?? {};
      if (!getSearchSuggestionsNg) {
        return [];
      }

      const products = getSearchSuggestionsNg.filter((item: any) => item.type === 'productName');
      const isRequestedPageAlreadyOpened = window.location.pathname.includes(
        products[0]?.attributesObject?.Slug,
      );

      if (
        /^\d{4,6}$/.test(query.value) &&
        products?.length === 1 &&
        getSearchSuggestionsNg.filter((item: any) => item.type === 'WebsiteContent')?.length === 0
      ) {
        if (
          query.value.padStart(6, '0') === products[0]?.attributesObject?.ID &&
          !isRequestedPageAlreadyOpened
        ) {
          const redirectUrl = productUrlBase.value + products[0]?.attributesObject?.Slug;

          if (queryEnter.value) {
            window.location.href = redirectUrl;
          } else {
            productRedirectUrl.value = redirectUrl;
          }
        } else {
          productRedirectUrl.value = null;
        }

        if (queryEnter.value && isRequestedPageAlreadyOpened) {
          navigationStore.setShowFlyout(false);
          navigationStore.setShowSearchSuggest(false);
          productRedirectUrl.value = null;
          query.value = '';
          queryTrimmed.value = '';
        }
      }

      if (queryEnter.value) {
        queryEnter.value = false;
      }

      return getSearchSuggestionsNg;
    };

    const [catalogSuggestions, contentSuggestions] = await Promise.all([
      hasProducts.value ? getSearchSuggestionsByChannel('catalog') : [],
      getSearchSuggestionsByChannel('content'),
    ]);
    const allSuggestions = [...catalogSuggestions, ...contentSuggestions];

    const currentQueryTrimmed = queryTrimmed.value;
    const currentSuggestionsQuery = suggestionsQuery.value;

    if (queryValue === currentQueryTrimmed && hasProducts.value) {
      searchSuggestions.value = allSuggestions;
      suggestionsQuery.value = queryValue;
      if (
        !(isFlyoutShown.value && isSearchSuggestShown.value && query.value !== queryValue) &&
        currentSuggestionsQuery !== queryValue
      ) {
        setShowSuggestionsFlyout(true);
      }
    } else if (!currentQueryTrimmed && isFlyoutShown.value) {
      setShowSuggestionsFlyout(false);
    }
  });

  const setQueryData = (data: {
    apollo: Client | undefined;
    brandId: string;
    factFinderChannel: string;
    locale: string;
    hasProducts: boolean;
    productUrlBase: string;
    distributionChannels?: string[] | null;
    supplier?: string;
    publicAssortment?: boolean;
  }) => {
    apollo.value = data.apollo ?? null;
    brandId.value = data.brandId;
    distributionChannels.value = data.distributionChannels || null;
    supplier.value = data.supplier;
    publicAssortment.value = Boolean(data.publicAssortment);
    locale.value = data.locale;
    hasProducts.value = data.hasProducts;
    factFinderChannel.value = data.factFinderChannel;
    productUrlBase.value = data.productUrlBase;
  };

  const setQuery = (queryParam: string | { value?: string; noShowSuggestions?: boolean }) => {
    const queryValue = typeof queryParam === 'string' ? queryParam : queryParam.value || '';
    query.value = queryValue;
    queryTrimmed.value = queryValue.trim();
    queryEnter.value = false;
    productRedirectUrl.value = '';

    const noShowSuggestions = typeof queryParam !== 'string' && queryParam.noShowSuggestions;
    const showSuggestions =
      !noShowSuggestions && requestSuggestions.value && queryTrimmed.value !== '';

    if (!showSuggestions) {
      inhibitSuggestions.value = true;
      if (isSearchSuggestShown.value) {
        setShowSuggestionsFlyout(false);
      }
      inhibitSuggestions.value = false;
      return;
    }

    getSearchSuggestionsThrottled(queryTrimmed.value);
  };

  const setQueryEnter = (value: boolean) => {
    queryEnter.value = value;
  };

  const setRequestSuggestions = (value: boolean) => {
    requestSuggestions.value = value;
  };

  const setProductUrlBase = (value: string) => {
    productUrlBase.value = value;
  };

  const setProductRedirectUrl = (value: string | null) => {
    productRedirectUrl.value = value;
  };

  const setSearchSuggestions = (suggestions: any[], queryValue = '') => {
    searchSuggestions.value = suggestions || [];
    suggestionsQuery.value = queryValue;
  };

  return {
    query,
    queryTrimmed,
    queryEnter,
    searchSuggestions,
    suggestionsQuery,
    requestSuggestions,
    inhibitSuggestions,
    apollo,
    locale,
    brandId,
    factFinderChannel,
    hasProducts,
    productUrlBase,
    productRedirectUrl,
    setQueryData,
    setShowSuggestionsFlyout,
    setQuery,
    setQueryEnter,
    setRequestSuggestions,
    setProductUrlBase,
    setProductRedirectUrl,
    setSearchSuggestions,
  };
});
